import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PencilLine, RefreshCcw } from "lucide-react";

import { Search, Package, Loader, ChevronDown, Settings } from "lucide-react";
import { Alert, Box, Snackbar } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ProductsModalBottomTables from "./ProductsModalBottomTables";
import AllDoSidebar from "./AllDoSidebar";
import GenericComponent from "./GenericComponent";
import ReuseSidebar from "./ReuseSidebar";
import CommonModal from "../common/CommonModal";
import ProductInfoSidebar from "./ProductsInfoSidebar";
import ColumnManagingModal from "./ColumnManagingModal";
import { generateColumnsFromData } from "../../utils/constants";
import ProductsActionsDropdown from "./ProductsModalActionButtons";
import useColumnManager from "../../hooks/useColumnManager";
import useApi from "../../hooks/useApi";
import canUserSeeThis from "../common/CheckCanUserSeeThis";
import checkIfUserHasAccess from "../common/UserCheckAccess";
import SalesmanRequestModal from "./SalesmanRequestModal";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ProductListing = ({
  handleProductSelect,
  modalSuggestions = [],
  onSearchQueryChange,
  loading,
  setSearchBy,
  searchBy,
  onRefresh,
  searchQuery,
  pageNumber,
  pageSize,
  totalRows,
  onPageChange,
  isFullPage = false,
}) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery || "");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stockSideBarOpen, setStockSidebarOpen] = useState(false);
  const [activeComponent, setActiveComponent] = useState(null);
  const [showCommonModal, setShowCommonModal] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [rows, setRows] = useState([]);
  const [currentRowIndex, setCurrentRowIndex] = useState(0);
  const dataGridRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error | warning | info
  });

  const [refreshingStock, setRefreshingStock] = useState(false);

  const COMPANY_CODE = localStorage.getItem("companyCode");
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const currentUserObject = JSON.parse(localStorage.getItem("currentUser"));
  const CURRENT_USER_ROLE = currentUserObject[0].accessgrp;
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const { request } = useApi();
  const IMAGE_PATH = API_PATHS[0].IMG_API_PATH;
  // Default column percentages for products table
  const defaultColumnPercents = {
    Code: 15,
    Description: 25,
    Unit: 10,
    Stock: 10,
    Price: 12,
    Category: 15,
    Brand: 13,
  };

  const columnManagerData = useMemo(() => modalSuggestions, [modalSuggestions]);
  const {
    tableRef,
    columnVisibilityModel,
    columnsOrder,
    showColumnManager,
    setShowColumnManager,
    draggedColumn,
    dragOverIndex,
    isDragging,
    finalColumns,
    handleColumnResize,
    handleVisibilityChange,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    moveColumn,
    resetColumnOrder,
    toggleColumnVisibility,
    getColumnDisplayName,
  } = useColumnManager({
    data: columnManagerData,
    screenCode: "PRODUCT_LISTING",
    baseUrl: BASE_URL,
    companyCode: COMPANY_CODE,
    defaultColumnPercents,
    // defaultColumnsOrder: [
    //   "Code",
    //   "Imagepath",
    //   "Description",
    //   "Unit",
    //   "Stock",
    //   "Price",
    //   "Category",
    //   "Brand",
    // ],
    generateColumnsFunction: (data) => generateColumnsFromData(data),
    request,
    imagePath: BASE_URL,
  });

  const processedRows = useMemo(() => {
    if (modalSuggestions && modalSuggestions.length > 0) {
      return modalSuggestions.map((item, index) => ({
        // Use stable item.Code as the ID if available, otherwise fall back to row-index
        id: item.Code || `row-${index}`,
        ...item,
      }));
    }
    return [];
  }, [modalSuggestions]);

  useEffect(() => {
    setRows(processedRows);
    if (processedRows.length === 0) {
      setSelectedProduct(null);
      setSelectedRowIds([]);
      setCurrentRowIndex(0);
    } else if (processedRows.length > 0) {
      // Auto-select first row when data loads
      setCurrentRowIndex(0);
      setSelectedProduct(processedRows[0]);
      setSelectedRowIds([processedRows[0].id]);
    }
  }, [processedRows]);

  const modeMap = {
    AllTransactions: "All_Transactions",
    DeliveryOrder: "Dorder",
    SalesOrder: "Sorder",
    Quotations: "Quot",
    QuotationHistory: "Quot",
    PendingOrder: "Porder",
    OrderRequest: "Orderreq",
    SalesmanRequest: "Smanreq",
    Claim: "Claim",
    Sales: "Sales",
    Purchase: "Purchase",
    QuotationTrack: "Quot_Track",
    Transit: "Transit",
    ItemDetails: "ItemDetails",
  };
  const searchCounterRef = useRef(0);
  const lastAppliedCounterRef = useRef(0);
  const initiatedByUs = useRef(false);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (onSearchQueryChange) {
        searchCounterRef.current += 1;
        const queryToSend =
          searchTerm?.trim() === "" ? "-" : searchTerm?.trim();
        initiatedByUs.current = true;
        onSearchQueryChange(queryToSend);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Sync parent-provided `searchQuery` into local input when it changes
  useEffect(() => {
    if (typeof searchQuery === "string" && !initiatedByUs.current) {
      setSearchTerm(searchQuery === "-" ? "" : searchQuery);
    }
    initiatedByUs.current = false;
  }, [searchQuery]);

  // Arrow key navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Handle arrow keys for row navigation
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (rows.length === 0) return;
        let newIndex = currentRowIndex;
        if (event.key === "ArrowDown") {
          newIndex = Math.min(currentRowIndex + 1, rows.length - 1);
        } else if (event.key === "ArrowUp") {
          newIndex = Math.max(currentRowIndex - 1, 0);
        }
        if (newIndex !== currentRowIndex) {
          setCurrentRowIndex(newIndex);
          const newSelectedRow = rows[newIndex];
          setSelectedProduct(newSelectedRow);
          setSelectedRowIds([newSelectedRow.id]);
          setStockSidebarOpen(true);
        }
        return;
      }

      // Handle Alt+O for Salesman Request
      if (
        event.altKey &&
        (event.key === "o" || event.key === "O") &&
        selectedProduct
      ) {
        event.preventDefault();
        // Open CommonModal for Salesman Request
        setShowCommonModal(true);
        return;
      }

      // Handle function keys (F1-F12)
      if (
        event.key.startsWith("F") &&
        event.key.length > 1 &&
        selectedProduct
      ) {
        event.preventDefault();
        console.log("Function key pressed:", event.key);
        if (event.key === "F3") {
          if (canUserSeeThis("AE")) {
            handleNavigateEdit(selectedProduct);
            return;
          } else {
            console.log("no access");
            return;
          }
        }
        const functionKeys = {
          F1: {
            component: "SalesOrder",
            label: "Sales Order Pending",
            fullLabel: "Sales Order Pending",
            button: "F1",
          },
          F2: {
            component: "DeliveryOrder",
            label: "Delivery Order Pending",
            fullLabel: "Delivery Order Pending",
            button: "F2",
          },
          F3: {
            component: "Itemdetails",
            label: "Item details with modification (upon rights)",
            fullLabel: "Item details with modification (upon rights)",
            button: "F3",
          },
          F4: {
            component: "OrderRequest",
            label: "Order request",
            fullLabel: "Order Request",
            button: "F4",
          },
          F7: {
            component: "Sales",
            label: "Sales History",
            fullLabel: "Sales History",
            button: "F7",
          },
          F8: {
            component: "Purchase",
            label: "Purchase History",
            fullLabel: "Purchase History",
            button: "F8",
          },
          F9: {
            component: "AllTransactions",
            label: "Complete Transaction History",
            fullLabel: "Complete Transaction History",
            button: "F9",
          },
          F10: {
            component: "PendingOrder",
            label: "Pending Purchase Order",
            fullLabel: "Pending Purchase Order",
            button: "F10",
          },
          F11: {
            component: "QuotationHistory",
            label: "Quotation History",
            fullLabel: "Quotation History",
            button: "F11",
          },
          F12: {
            component: "OrderRequestHistory",
            label: "Order Request History",
            fullLabel: "Order Request History",
            button: "F12",
          },
        };
        // Mapping of Function Keys to App Privileges.
        let accessKeyAndFunctionKeyMap = {
          F1: "",
          F2: "JT",
          // F3 is handled globally above
          F4: "JS",
          F7: "JO",
          F8: "JN",
          F9: "JV",
          F11: "JU",
        };

        let requiredKey = accessKeyAndFunctionKeyMap[event.key];

        // Block keys that are not explicitly matched in the new requirement mapped dictionary.
        if (requiredKey === undefined) {
          return;
        }

        let result = requiredKey === "" ? true : canUserSeeThis(requiredKey);
        if (result) {
          const buttonConfig = functionKeys[event.key];
          if (buttonConfig) {
            setActiveComponent(buttonConfig);
            setSidebarOpen(true);
          }
        } else {
          console.log("else of result");
        }
      }

      // Handle Enter key
      if (event.key === "Enter" && selectedProduct) {
        if (handleProductSelect) {
          handleProductSelect(selectedProduct);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedProduct, rows, currentRowIndex, handleProductSelect]);

  const handleRowClick = (params) => {
    const clickedIndex = rows.findIndex((row) => row.id === params.row.id);
    setCurrentRowIndex(clickedIndex);
    setSelectedProduct(params.row);
    setStockSidebarOpen(true);
  };

  const handleButtonClick = (button) => {
    setActiveComponent({
      component: button.component,
      label: button.label,
      fullLabel: button.fullLabel,
      button: button.button,
    });
    setSidebarOpen(true);
  };

  const handleRowSelectionChange = (newSelection) => {
    setSelectedRowIds(newSelection);
    if (newSelection.length > 0) {
      const selectedRow = rows.find((row) => row.id === newSelection[0]);
      const selectedIndex = rows.findIndex((row) => row.id === newSelection[0]);
      setCurrentRowIndex(selectedIndex);
      setSelectedProduct(selectedRow || null);
    } else {
      setSelectedProduct(null);
    }
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
    setActiveComponent(null);
  };

  const handleSearchByChange = (e) => {
    const value = e.target.value;
    setSearchBy(value);
  };

  const renderSidebarContent = () => {
    if (!activeComponent) return null;

    const mode = modeMap[activeComponent.component];
    const componentProps = { selectedProduct, mode };

    switch (activeComponent.component) {
      case "DeliveryOrder":
      case "PendingOrder":
      case "SalesOrder":
      case "Sales":
      case "SalesmanRequest":
      case "Purchase":
      case "AllTransactions":
      case "Transit":
      case "QuotationTrack":
      case "Quotations":
      case "QuotationHistory":
      case "OrderRequest":
      case "Claim":
        return (
          <AllDoSidebar
            {...componentProps}
            exportFileName={activeComponent?.fullLabel}
          />
        );
      default:
        return (
          <GenericComponent title={activeComponent.label} {...componentProps} />
        );
    }
  };

  const navigate = useNavigate();
  const handleNavigateEdit = (selectedProduct) => {
    console.log("selectedProduct EDIT:", selectedProduct);

    if (selectedProduct?.Code) {
      const route = "/itemCreation";

      navigate(route, { state: selectedProduct.Code });
    }
  };
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleStockRefresh = async () => {
    if (!COMPANY_CODE) {
      showSnackbar("Company code missing", "error");
      return;
    }

    setRefreshingStock(true);

    try {
      const response = await fetch(
        `${BASE_URL}/api/CRMStockRefresh/${COMPANY_CODE}`,
        { method: "GET" },
      );

      if (!response.ok) {
        throw new Error("Request failed");
      }

      const result = await response.json();

      if (Array.isArray(result) && result[0]?.Column1 === "DONE") {
        showSnackbar("Stock refreshed successfully", "success");

        // Refresh the product list
        if (onRefresh) {
          onRefresh();
        }
      } else {
        showSnackbar(
          `Stock refresh failed: ${result[0]?.Column1 || "Unknown response"}`,
          "warning",
        );
      }
    } catch (error) {
      console.error(error);
      showSnackbar("Stock refresh aborted or network error ", "error");
    } finally {
      setRefreshingStock(false);
    }
  };

  return (
    <div
      className={`w-full bg-gray-50 flex flex-col ${isFullPage ? "min-h-screen" : "h-full"}`}
    >
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:p-5 p-2 min-w-0 w-full">
        {/* Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden w-full mb-4">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex lg:flex-row flex-col items-center justify-between lg:gap-4 gap-y-2">
              <div className="flex-1 lg:max-w-2xl">
                <div className="flex lg:flex-row flex-col items-center gap-2">
                  <div className="relative flex-1 lg:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchTerm}
                      autoFocus
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-32 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                    <div className="absolute right-24 top-2 bottom-2 w-px bg-gray-300"></div>
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <select
                        value={searchBy}
                        onChange={handleSearchByChange}
                        className="appearance-none bg-transparent border-none text-xs font-medium text-gray-600 focus:outline-none cursor-pointer pr-5"
                      >
                        <option value="UNIVERSAL">Default</option>
                        <option value="universalcode">Code</option>
                        <option value="universalDESC">Description</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pointer-events-none">
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleStockRefresh}
                  disabled={refreshingStock}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2
    ${
      refreshingStock
        ? "bg-gray-400 cursor-not-allowed text-white"
        : "bg-red-600 hover:bg-red-700 text-white"
    }`}
                >
                  <RefreshCcw
                    className={`w-4 h-4 ${
                      refreshingStock ? "animate-spin" : ""
                    }`}
                  />
                  {refreshingStock ? "Refreshing..." : "Refresh"}
                </button>

                {canUserSeeThis("AE") && (
                  <button
                    disabled={!selectedProduct?.Code}
                    onClick={() => handleNavigateEdit(selectedProduct)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2
    ${
      selectedProduct?.Code
        ? "bg-blue-600 hover:bg-blue-700 text-white"
        : "bg-gray-300 cursor-not-allowed text-gray-500"
    }`}
                  >
                    <PencilLine className="w-4 h-4" />
                    <span>{canUserSeeThis("AE") ? "Edit" : "View"} </span>
                  </button>
                )}

                <ProductsActionsDropdown
                  onButtonClick={handleButtonClick}
                  selectedProduct={selectedProduct}
                />
                {(CURRENT_USER_ROLE?.toLowerCase() === "admin" ||
                  CURRENT_USER_ROLE?.toLowerCase() === "coordinator" ||
                  checkIfUserHasAccess("GV")) && (
                  <button
                    onClick={() => setShowColumnManager(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Columns</span>
                  </button>
                )}
                {/* {selectedProduct && handleProductSelect && (
                  <button
                    onClick={() => handleProductSelect(selectedProduct)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    Select
                  </button>
                )} */}
              </div>
            </div>
          </div>

          <div
            className="w-full transition-all duration-200"
            style={{
              height: isFullPage ? "calc(100vh - 420px)" : "calc(60vh - 120px)",
              minHeight: isFullPage ? "400px" : "280px",
              maxHeight: isFullPage ? "none" : "480px",
            }}
            ref={tableRef}
          >
            <Box
              sx={{
                height: "100%",
                width: "100%",
                minWidth: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DataGrid
                ref={dataGridRef}
                rows={rows}
                columns={finalColumns}
                loading={loading}
                onRowClick={handleRowClick}
                onRowDoubleClick={(params) => {
                  if (handleProductSelect) {
                    handleProductSelect(params.row);
                  }
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleVisibilityChange}
                onColumnResize={handleColumnResize}
                rowSelectionModel={selectedRowIds}
                paginationMode="server"
                rowCount={totalRows}
                disableColumnMenu
                rowHeight={40}
                rowBuffer={10}
                columnBuffer={5}
                onRowSelectionModelChange={handleRowSelectionChange}
                sx={{
                  border: "none",
                  flex: 1,
                  fontFamily: '"Figtree", sans-serif',
                  "& .MuiDataGrid-footerContainer": {
                    display: "none",
                  },
                  "& .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
                slots={{
                  noRowsOverlay: () => (
                    <div className="flex flex-col items-center justify-center h-full space-y-4 bg-gray-50">
                      <Package className="h-16 w-16 text-gray-300" />
                      <h3 className="text-lg font-semibold text-gray-600">
                        No products found
                      </h3>
                      <p className="text-gray-400">
                        Try adjusting your search criteria
                      </p>
                    </div>
                  ),
                  loadingOverlay: () => (
                    <div className="flex flex-col items-center justify-center h-full space-y-3">
                      <Loader className="animate-spin h-8 w-8 text-gray-600" />
                      <span className="text-gray-500 font-medium">
                        Loading products...
                      </span>
                    </div>
                  ),
                }}
              />

              {/* CUSTOM PAGINATION COMPONENT - Placed exactly where the old footer was */}
              <div className="flex items-center justify-end px-6 py-2.5 space-x-6 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-500 font-medium font-['Figtree']">
                  <span className="text-gray-900">
                    {(pageNumber - 1) * pageSize + 1}
                  </span>
                  <span className="mx-1">-</span>
                  <span className="text-gray-900">
                    {Math.min(pageNumber * pageSize, totalRows)}
                  </span>
                  <span className="mx-1 text-gray-400 text-xs italic font-light">
                    of
                  </span>
                  <span className="text-gray-900">{totalRows}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onPageChange(pageNumber - 1)}
                    disabled={pageNumber === 1 || loading}
                    className="p-1.5 rounded-md hover:bg-white hover:shadow-sm disabled:opacity-20 disabled:cursor-not-allowed transition-all border border-transparent hover:border-gray-200"
                  >
                    <ChevronLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <button
                    onClick={() => onPageChange(pageNumber + 1)}
                    disabled={pageNumber * pageSize >= totalRows || loading}
                    className="p-1.5 rounded-md hover:bg-white hover:shadow-sm disabled:opacity-20 disabled:cursor-not-allowed transition-all border border-transparent hover:border-gray-200"
                  >
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </Box>
          </div>
        </div>

        {/* Bottom Info Tables */}
        <ProductsModalBottomTables
          selectedProduct={selectedProduct}
          BASE_URL={BASE_URL}
        />

        {checkIfUserHasAccess("FT") && (
          <ProductInfoSidebar
            selectedProduct={selectedProduct}
            isVisible={stockSideBarOpen}
            onClose={() => setStockSidebarOpen(false)}
            BASE_URL={BASE_URL}
          />
        )}

        <ReuseSidebar
          isOpen={sidebarOpen}
          onClose={closeSidebar}
          title={
            activeComponent?.fullLabel || activeComponent?.label || "Details"
          }
          button={activeComponent?.button || ""}
          selectedProduct={selectedProduct}
          width={1000}
        >
          {renderSidebarContent()}
        </ReuseSidebar>

        {/* Column Managing Modal */}
        <ColumnManagingModal
          showColumnManager={showColumnManager}
          setShowColumnManager={setShowColumnManager}
          columnVisibilityModel={columnVisibilityModel}
          columnsOrder={columnsOrder}
          handleDragEnd={handleDragEnd}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          isDragging={isDragging}
          dragOverIndex={dragOverIndex}
          draggedColumn={draggedColumn}
          toggleColumnVisibility={toggleColumnVisibility}
          resetColumnOrder={resetColumnOrder}
          getColumnDisplayName={getColumnDisplayName}
          moveColumn={moveColumn}
        />
      </div>
      {/* CommonModal for Salesman Request (Alt+O) */}
      <CommonModal
        isOpen={showCommonModal}
        onClose={() => setShowCommonModal(false)}
        title="Salesman Request"
        size="xl"
      >
        <SalesmanRequestModal
          selectedProduct={selectedProduct}
          BASE_URL={BASE_URL}
        />
      </CommonModal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProductListing;
